import { ActionCard, AiPlayContextParams, AiTeammateRealtimeModel } from '../interfaces'
import { CrmObject } from '../interfaces/ConnectToCrmWidget'
import { CrmObjectType } from '../interfaces/Integration'

interface BackendTokenResponse {
  token: string
  expiresAt: number
}

/**
 * Client for interacting with the Playbooks Backend Service API.
 * Handles token management and makes direct calls to the Playbooks Backend Service.
 * See `auth.md` in Playbooks Backend Service docs for details.
 */
export class BackendClient {
  private backendUrl?: string
  private tokenIssuerEndpoint?: string
  private tokenIssuerApiToken?: string

  constructor({
    backendUrl,
    tokenIssuerEndpoint,
    tokenIssuerApiToken,
  }: {
    /** Playbooks Backend Service base URL */
    backendUrl?: string
    /** URL of the API endpoint to fetch Playbooks Backend Service tokens from */
    tokenIssuerEndpoint?: string
    /** Internal API token to authenticate backend service token requests */
    tokenIssuerApiToken?: string
  }) {
    this.backendUrl = backendUrl
    this.tokenIssuerEndpoint = tokenIssuerEndpoint
    this.tokenIssuerApiToken = tokenIssuerApiToken
  }

  /**
   * Generate text using LLM based on the provided prompt.
   */
  public async generateText(params: GenerateTextParams): Promise<string> {
    const result = await this.fetchJson<{ text: string }>('ai/text', {
      method: 'POST',
      body: JSON.stringify({
        prompt: params.prompt,
        model: params.model,
        'system-prompt': params.systemPrompt,
        'reasoning-effort': params.reasoningEffort,
        playId: params.playId,
        playSource: params.playSource,
        templateId: params.templateId,
        fileIds: params.fileIds,
        includeAiScope: params.includeAiScope,
      }),
    })

    return result.text
  }

  /**
   * Generate text using LLM and stream text chunks as they arrive.
   */
  public async generateTextStream(params: GenerateTextParams, onChunk: (chunk: string) => void): Promise<string> {
    const response = await this.fetch('ai/text', {
      method: 'POST',
      body: JSON.stringify({
        prompt: params.prompt,
        model: params.model,
        'system-prompt': params.systemPrompt,
        'reasoning-effort': params.reasoningEffort,
        playId: params.playId,
        playSource: params.playSource,
        templateId: params.templateId,
        fileIds: params.fileIds,
        includeAiScope: params.includeAiScope,
        stream: true,
      }),
    })

    if (!response.body) {
      throw new Error('Streaming response body is not available')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let generatedText = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      const chunk = decoder.decode(value, { stream: true })
      generatedText += chunk
      onChunk(chunk)
    }

    const finalChunk = decoder.decode()
    if (finalChunk) {
      generatedText += finalChunk
      onChunk(finalChunk)
    }

    return generatedText
  }

  /**
   * Convert text to speech and get a streaming URL.
   */
  public async generateSpeech(params: SpeechGenerationParams): Promise<string> {
    const result = await this.fetchJson<{ requestId: string }>('ai/speech', {
      method: 'POST',
      body: JSON.stringify(params),
    })

    return `${this.getBackendUrl()}/v1/ai/speech?requestId=${result.requestId}`
  }

  /**
   * Generate icons for assessment responses using AI.
   */
  public async generateAssessmentIcons(question: string, responses: string[]): Promise<string[]> {
    const result = await this.fetchJson<{ icons: string[] }>('ai/playbooks/assessment-icons', {
      method: 'POST',
      body: JSON.stringify({ question, responses }),
    })

    return result.icons
  }

  /**
   * Generate responses for a play using LLM.
   */
  public async generatePlayResponses(params: GeneratePlayResponsesParams): Promise<GeneratedPlayResponse[]> {
    const result = await this.fetchJson<unknown>('ai/plays/responses', {
      method: 'POST',
      body: JSON.stringify(params),
    })

    return parseGeneratedPlayResponses(result)
  }

  /**
   * Initiate a realtime AI session for the Teammate Widget.
   */
  public async createTeammateSession(params: TeammateSessionParams): Promise<TeammateSessionResponse> {
    return this.fetchJson<TeammateSessionResponse>('ai/plays/teammate/session', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  /**
   * Upload collected AI Teammate Realtime usage stats batch for a session.
   */
  public async updateTeammateSessionStats(
    sessionId: number,
    params: UpdateTeammateSessionStatsParams,
  ): Promise<UpdateTeammateSessionStatsResponse> {
    return this.fetchJson<UpdateTeammateSessionStatsResponse>(`ai/plays/teammate/sessions/${sessionId}/stats`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  /**
   * Fetch a fresh token from the API.
   */
  private async getToken(): Promise<string> {
    if (!this.tokenIssuerEndpoint) {
      throw new Error('Token issuer endpoint is not configured')
    }

    const response = await fetch(this.tokenIssuerEndpoint, {
      method: 'POST',
      headers: {
        ...(this.tokenIssuerApiToken ? { Authorization: `Bearer ${this.tokenIssuerApiToken}` } : {}),
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch Playbooks token: ${response.statusText}`)
    }

    const data: BackendTokenResponse = await response.json()
    return data.token
  }

  /**
   * Make an authenticated request to the Playbooks Backend Service, assuming JSON response type.
   */
  private async fetchJson<T>(path: string, options: RequestInit = {}): Promise<T> {
    const response = await this.fetch(path, options)

    return response.json()
  }

  /**
   * Make an authenticated request to the Playbooks Backend Service.
   */
  private async fetch(path: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getToken()
    const backendUrl = this.getBackendUrl()

    const { headers, ...restOptions } = options
    const response = await fetch(`${backendUrl}/v1/${path}`, {
      ...restOptions,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...headers,
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Playbooks Backend Service request failed:', {
        status: response.status,
        path,
        body: errorText,
      })
      throw new Error(errorText || response.statusText || 'API request failed')
    }

    return response
  }

  private getBackendUrl(): string {
    if (!this.backendUrl) {
      throw new Error('Playbooks Backend Service base URL is not configured')
    }

    return this.backendUrl
  }

  // #region Integrations

  public get integrations() {
    return {
      findCrmObjects: this.findCrmObjects.bind(this),
      createCrmObjects: this.createCrmObjects.bind(this),
      syncCrmObjects: this.syncCrmObjects.bind(this),
    }
  }

  /** Search for matching records in the CRM system. */
  private async findCrmObjects(params: { playId: string; widgetId: string; actionCard: ActionCard }) {
    const result = await this.fetchJson<CrmObject[]>('integrations/find-crm-objects', {
      method: 'POST',
      body: JSON.stringify(params),
    })

    return result
  }

  /** Create new records in the CRM system. */
  private async createCrmObjects(params: {
    playId: string
    widgetId: string
    actionCard: ActionCard
    objectTypes: CrmObjectType[]
  }) {
    if (!params.objectTypes.length) return []

    const result = await this.fetchJson<CrmObject[]>('integrations/create-crm-objects', {
      method: 'POST',
      body: JSON.stringify(params),
    })

    return result
  }

  /** Sync existing CRM records with our current action card. */
  private async syncCrmObjects(params: {
    playId: string
    widgetId: string
    actionCard: ActionCard
    objects: CrmObject[]
  }) {
    await this.fetch('integrations/sync-crm-objects', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  // #endregion
}

function parseGeneratedPlayResponses(result: unknown): GeneratedPlayResponse[] {
  const parsedResult = typeof result === 'string' ? JSON.parse(result) : result

  if (!Array.isArray(parsedResult)) {
    throw new Error('Playbooks Backend Service returned invalid generated play responses')
  }

  return parsedResult as GeneratedPlayResponse[]
}

export interface GenerateTextParams extends AiPlayContextParams {
  /** The user prompt to guide the AI's response */
  prompt: string
  /** Optional AI model to use */
  model?: string
  /** Optional system prompt */
  systemPrompt?: string
  /** Optional reasoning effort level */
  reasoningEffort?: string
  /** IDs of template-owned attachments to include in the request */
  fileIds?: string[]
  /** Whether the template's PDF scope files should be included */
  includeAiScope?: boolean
}

export interface GeneratePlayResponsesParams extends AiPlayContextParams {
  /** The empty part of the Action card that the AI needs to fill out */
  actionCard: string
  /** Optional user prompt to guide the AI's response */
  prompt?: string
  /** Optional knowledge context for the AI */
  knowledge?: string
  /** Optional AI model to use */
  model?: string
  /** Optional reasoning effort level */
  reasoningEffort?: string
  /** IDs of template-owned attachments to include in the request */
  fileIds?: string[]
  /** Whether the template's PDF scope files should be included */
  includeAiScope?: boolean
}

export interface GeneratedPlayResponse {
  questionId: number
  answerIds?: any[]
  answer?: any
  confidence: number
  label?: string
  timestamp?: number
}

export interface SpeechGenerationParams {
  /** The text to convert to speech */
  text: string
  /** Optional voice (alloy, echo, fable, onyx, nova, shimmer) */
  voice?: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer'
  /** Optional model (tts-1 or tts-1-hd) */
  model?: 'tts-1' | 'tts-1-hd'
}

export interface TeammateSessionParams {
  /** The SDP offer from the client */
  sdp: string
  /** Serialized Action Card sections to provide context for the AI teammate */
  actionCardSections: string[]
  /** Client-local current date for resolving relative dates like "today" in the speech input */
  currentDate?: string
  /** Whether OpenAI should transcribe the input audio for transcript recording */
  recordTranscript?: boolean
  /** Whether OpenAI should call tools to fill out the playbook automatically */
  fillAutomatically?: boolean
  /** Realtime model preset selected for this teammate session */
  model?: AiTeammateRealtimeModel
}

export interface TeammateSessionResponse {
  sdpAnswer: string
  sessionId: number
}

export interface TeammateRealtimeStatsEvent {
  sequence: number
  eventAt: string
  responseId?: string
  usage: {
    totalTokens?: number
    inputTokens?: number
    outputTokens?: number
    inputTextTokens?: number
    inputAudioTokens?: number
    cachedInputTokens?: number
    cachedTextTokens?: number
    cachedAudioTokens?: number
    outputTextTokens?: number
    outputAudioTokens?: number
  }
}

export interface UpdateTeammateSessionStatsParams {
  events: TeammateRealtimeStatsEvent[]
  isFinal?: boolean
}

export interface UpdateTeammateSessionStatsResponse {
  acceptedEventCount: number
  lastAcceptedSequence: number
}
